/**
 * Folded sidebar (the rail) — design-reference lines 147–173, and its expanded companion (lines 174–204).
 * One of the five canonical surfaces (`kit.ts`): the rail defines the app's dark-rail vocabulary.
 */
import type { ReactNode } from 'react';
import { css, s } from './style';
import type { Handler, Primitive } from './controls';

/** The 68px column the rail lives in (line 148). */
export function RailColumn({ children }: { children?: ReactNode }) {
  return (
    <div
      style={css('flex:none;display:flex;flex-direction:column;gap:10px;padding:12px 0 12px 12px;width:68px')}
    >
      {children}
    </div>
  );
}
/** Brand tile (line 149) — 56×52, radius 18, rail ground. */
export function RailBrand({
  icon = 'ph-asterisk',
  title = 'PointFive OS',
}: {
  icon?: string;
  title?: string;
}) {
  return (
    <div
      title={title}
      style={css(
        'width:56px;height:52px;flex:none;border-radius:18px;background:var(--rail);color:var(--rail-ink);display:flex;align-items:center;justify-content:center;box-shadow:var(--shadow-card)',
      )}
    >
      <i className={`ph-fill ${icon}`} style={css('font-size:20px')}></i>
    </div>
  );
}
/** The scrolling rail slab (line 150) — radius 22, rail ground, its own scroll viewport with edge fades. */
export function RailSlab({ children }: { children?: ReactNode }) {
  return (
    <div
      className="rail-slab"
      style={css(
        'position:relative;flex:0 1 auto;min-height:0;display:flex;flex-direction:column;width:56px;border-radius:22px;background:var(--rail);box-shadow:var(--shadow-card);overflow:hidden',
      )}
    >
      {children}
    </div>
  );
}
export function RailViewport({ children }: { children?: ReactNode }) {
  return (
    <div
      className="rail-viewport"
      style={css('position:relative;flex:1 1 auto;min-height:0;overflow:hidden;display:flex;width:56px')}
    >
      <div
        className="rail-scroll"
        style={css(
          'flex:1 1 auto;min-height:0;overflow-y:auto;padding:0 0 10px;display:flex;flex-direction:column;align-items:center;gap:4px;width:56px',
        )}
      >
        {children}
      </div>
      <span className="rail-fade rail-fade-top" aria-hidden="true"></span>
      <span className="rail-fade rail-fade-bottom" aria-hidden="true">
        <i className="ph ph-caret-down"></i>
      </span>
    </div>
  );
}
/** Fold toggle inside the slab (line 151) — 34×34 on the rail ground. */
export function RailFoldButton({
  icon,
  title,
  onClick,
  hoverClass,
  ...aria
}: {
  icon: string;
  title?: string;
  onClick?: Handler;
  hoverClass?: string;
  'aria-label'?: string;
}) {
  return (
    <button
      className={hoverClass}
      onClick={onClick}
      title={title}
      style={css(
        'flex:none;align-self:center;margin-top:10px;width:34px;height:34px;border-radius:11px;border:none;background:transparent;color:var(--rail-ink);cursor:pointer;display:flex;align-items:center;justify-content:center',
      )}
      {...aria}
    >
      <i className={`ph ${s(icon)}`} style={css('font-size:18px')}></i>
    </button>
  );
}
/** Hairline under the fold button (line 152). */
export function RailDivider() {
  return (
    <div
      style={css(
        'flex:none;align-self:center;width:22px;height:1px;background:var(--rail-line);margin:5px 0 7px',
      )}
    ></div>
  );
}
/** Rail nav button (line 156) — 38×38, radius 12; the active one takes `--rail-on`. */
export function RailButton({
  icon,
  bg,
  fg,
  title,
  onClick,
  hoverClass,
  ...aria
}: {
  icon: Primitive;
  bg: Primitive;
  fg: Primitive;
  title?: string;
  onClick?: Handler;
  hoverClass?: string;
  'aria-label'?: string;
}) {
  return (
    <button
      className={hoverClass}
      onClick={onClick}
      title={title}
      style={css(
        `width:38px;height:38px;border-radius:12px;border:none;background:${s(bg)};color:${s(fg)};cursor:pointer;display:flex;align-items:center;justify-content:center`,
      )}
      {...aria}
    >
      <i className={`ph ${s(icon)}`} style={css('font-size:18px')}></i>
    </button>
  );
}
/** The mode spacer (line 161) — a soft slab carrying the vertical mode label, collapsing to a glyph when short. */
export function RailSpacer({ label, icon }: { label: ReactNode; icon: string }) {
  return (
    <div
      className="rail-spacer"
      style={css(
        'flex:1 1 auto;min-height:44px;container-type:size;border-radius:22px;background:var(--rail-soft);display:flex;align-items:center;justify-content:center;width:56px;overflow:hidden',
      )}
    >
      <span
        className="rail-label"
        style={css(
          'writing-mode:vertical-rl;transform:rotate(180deg);font-size:9px;letter-spacing:0.22em;text-transform:uppercase;color:var(--muted2);font-weight:600;white-space:nowrap',
        )}
      >
        {label}
      </span>
      <i className={`ph ${icon} rail-mode-glyph`} style={css('font-size:16px;color:var(--muted2)')}></i>
    </div>
  );
}
/** The rail footer (line 166) — theme toggle + settings on a soft slab. */
export function RailFooter({ children }: { children?: ReactNode }) {
  return (
    <div
      style={css(
        'flex:none;margin-top:auto;border-radius:22px;background:var(--rail-soft);padding:10px 0;display:flex;flex-direction:column;align-items:center;gap:6px;width:56px',
      )}
    >
      {children}
    </div>
  );
}

/* ── Expanded sidebar (lines 174–204) — the rail's companion ────────────────────────────────────────────────── */
/** The 262px panel (line 175). */
export function SidebarPanel({ children }: { children?: ReactNode }) {
  return (
    <aside
      style={css(
        'width:262px;flex:none;display:flex;flex-direction:column;margin:12px 0 12px 12px;border-radius:20px;background:var(--surface);border:1px solid var(--card-line);box-shadow:var(--shadow-card);overflow:hidden',
      )}
    >
      {children}
    </aside>
  );
}
/** Brand tile in the expanded sidebar (line 177) — 38×38 on the rail ground. */
export function BrandTile({ icon = 'ph-asterisk' }: { icon?: string }) {
  return (
    <div
      style={css(
        'width:38px;height:38px;border-radius:12px;background:var(--rail);color:var(--rail-ink);display:flex;align-items:center;justify-content:center;flex:none',
      )}
    >
      <i className={`ph-fill ${icon}`} style={css('font-size:19px')}></i>
    </div>
  );
}
/** A nav row (line 186) — icon, label, optional count. */
export interface NavRowProps {
  icon: string;
  label: ReactNode;
  count?: ReactNode;
  bd: Primitive;
  bg: Primitive;
  fg: Primitive;
  fw?: Primitive;
  ic?: Primitive;
  onClick?: Handler;
  hoverClass?: string;
}
export function NavRow({ icon, label, count, bd, bg, fg, fw = '500', ic, onClick, hoverClass }: NavRowProps) {
  return (
    <button
      className={hoverClass}
      onClick={onClick}
      style={css(
        `display:flex;align-items:center;gap:11px;width:100%;padding:9px 11px;border-radius:11px;border:1px solid ${s(bd)};background:${s(bg)};color:${s(fg)};font-size:13px;font-weight:${s(fw)};cursor:pointer;text-align:left;margin-bottom:1px`,
      )}
    >
      <i className={`ph ${icon}`} style={css(`font-size:17px;color:${s(ic)}`)}></i>
      <span style={css('flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis')}>{label}</span>
      {count === undefined ? null : (
        <span style={css('font-size:11px;color:var(--muted2);font-variant-numeric:tabular-nums')}>
          {count}
        </span>
      )}
    </button>
  );
}
